/**
 * Shared helpers and constants for Transaction Lookup page.
 */
import dayjs from 'dayjs';
import type { TransactionDetail, AutoDetectedLog } from '@/common-types';

export const SYNC_STEPS_VS_LOG_NAMES: Record<string, string[] | null> = {
  sourceExport: ['exports.zip'],
  validateTfvars: null,
  exportFlows: ['exportFlowsSuccess.zip', 'exportFlowsErrors.zip'],
  destExport: ['exports.zip'],
  setupImport: null,
  'importOrg.1st-import': ['1st-import.zip'],
  publishSkeletonFlows: ['publishSkeletonFlows.zip'],
  firstPublishFlowsFromSource: ['firstPublishFlowsFromSource.zip', 'firstPublishFlowsFromSourceErrors.zip', 'firstPublishFlowsFromSourceSuccess.zip'],
  'importOrg.2nd-import': ['2nd-import.zip'],
  emailNotification: null,
};

export function getLogsForStep(stepName: string, logs: AutoDetectedLog[]): { fileName: string; url: string }[] {
  const logNames = SYNC_STEPS_VS_LOG_NAMES[stepName];
  if (!logNames || logs.length === 0) return [];
  return logNames.reduce<{ fileName: string; url: string }[]>((acc, logName) => {
    const match = logs.find((l) => l.path.endsWith(logName));
    if (match) acc.push({ fileName: logName, url: match.presignedUrl });
    return acc;
  }, []);
}

export function downloadUrlAsFile(url: string, fileName: string): Promise<void> {
  return fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      return res.blob();
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(objectUrl);
    })
    .catch((err) => {
      window.open(url, '_blank', 'noopener,noreferrer');
      const msg = err instanceof Error ? err.message : 'Download failed';
      return Promise.reject(new Error(`${msg}. Opened link in new tab — use the new tab to save the file.`));
    });
}

export const ACTION_DISPLAY_NAMES: Record<string, string> = {
  ember_adhoc_dr_selective_sync: 'Adhoc Selective Sync',
  ember_scheduled_dr_selective_sync: 'Scheduled Selective Sync',
  ember_adhoc_dr_full_sync: 'Adhoc Full Sync',
  ember_scheduled_dr_full_sync: 'Scheduled Full Sync',
  ember_adhoc_export: 'Adhoc Export',
  ember_scheduled_export: 'Scheduled Export',
  ember_adhoc_import: 'Adhoc Import',
  ember_scheduled_import: 'Scheduled Import',
  ember_adhoc_backup: 'Adhoc Backup',
  ember_scheduled_backup: 'Scheduled Backup',
  ember_adhoc_restore: 'Adhoc Restore',
  ember_scheduled_restore: 'Scheduled Restore',
};

export function formatActionName(action: string | undefined): string {
  if (!action) return '—';
  if (ACTION_DISPLAY_NAMES[action]) return ACTION_DISPLAY_NAMES[action];
  const lowerAction = action.toLowerCase();
  const match = Object.entries(ACTION_DISPLAY_NAMES).find(([key]) => key.toLowerCase() === lowerAction);
  if (match) return match[1];
  return action
    .replace(/^ember_/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatTimestamp(ts: string | undefined): string {
  if (!ts) return '—';
  const parsed = dayjs(ts);
  if (!parsed.isValid()) return ts;
  return parsed.format('MMM D, YYYY, h:mm A');
}

export function formatTimestampWithZones(ts: string | undefined): { local: string; utc: string } {
  if (!ts) return { local: '—', utc: '—' };
  const parsed = dayjs(ts);
  if (!parsed.isValid()) return { local: ts, utc: ts };

  const date = parsed.toDate();
  const formatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  return {
    local: new Intl.DateTimeFormat(undefined, formatOptions).format(date),
    utc: new Intl.DateTimeFormat('en-US', { ...formatOptions, timeZone: 'UTC' }).format(date),
  };
}

export function getOrgDisplay(
  t: TransactionDetail,
  key: 'sourceOrg' | 'destinationOrg'
): { name: string; id: string; region: string } | null {
  const orgs = t.exportOrgs;
  if (!orgs || typeof orgs !== 'object') return null;
  const org = orgs[key];
  if (!org || typeof org !== 'object') return null;
  const o = org as { name?: string; id?: string; orgId?: string; region?: string };
  return {
    name: o.name ?? '—',
    id: o.id ?? o.orgId ?? '—',
    region: o.region ?? '—',
  };
}

export function getTimeline(t: TransactionDetail): { started?: string; finished?: string } {
  return { started: t.dateStarted, finished: t.dateFinished };
}

export function getSuccessfulSteps(t: TransactionDetail): string[] {
  const steps = t.successfullSteps ?? t.successfulSteps;
  return Array.isArray(steps) ? steps : [];
}

export function getFailedSteps(t: TransactionDetail): string[] {
  const steps = t.failedSteps;
  return Array.isArray(steps) ? steps : [];
}

export function getOrgParity(t: TransactionDetail): number | null {
  const total = t.totalResourcesOnSourceOrg;
  const matching = t.totalMatchingResourcesOnDestOrg;
  if (total == null || total === 0 || matching == null) return null;
  return Math.round((matching / total) * 100);
}
